import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, TextareaHTMLAttributes } from 'react';

/**
 * AutoTextarea — a <textarea> that auto-grows to fit its content height,
 * so the user never has to scroll inside the box on a phone.
 *
 * Why this exists: pre-filled document sections (CPP, doc templates, RAMS
 * preset prose) often run 100–300 words. With a fixed `rows={5}`, a phone
 * user has to scroll inside the textarea to see the rest of the text and
 * then scroll back to edit — the original UX complaint.
 *
 * How it works:
 *   - Each render: reset height to 'auto', then set to scrollHeight.
 *     That's the canonical "auto-grow textarea" recipe and works in every
 *     browser, including WebView (Capacitor iOS/Android shells).
 *   - `minRows` prop sets the floor so an empty box isn't a single line.
 *
 * Drop-in replacement: same props as <textarea>, omit `rows` (use minRows
 * instead). Use ref to access the underlying <textarea> element.
 */
export interface AutoTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> {
  /** Floor height in rows. Empty boxes don't collapse below this. Default 3. */
  minRows?: number;
  /** Optional ceiling. Default 'none' — auto-grow forever. */
  maxRows?: number;
}

export const AutoTextarea = forwardRef<HTMLTextAreaElement, AutoTextareaProps>(function AutoTextarea(
  { minRows = 3, maxRows, value, style, onInput, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  // Expose the inner textarea via the forwarded ref so parents can still
  // call .focus(), measure scroll, etc.
  useImperativeHandle(ref, () => innerRef.current!, []);

  function resize(el: HTMLTextAreaElement) {
    // Reset first so the textarea can shrink as well as grow.
    el.style.height = 'auto';
    let target = el.scrollHeight;
    if (maxRows != null) {
      // approximate line height from computed style; fall back to 20px.
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight) || 20;
      const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const cap = lh * maxRows + padY;
      if (target > cap) {
        target = cap;
        el.style.overflowY = 'auto';
      } else {
        el.style.overflowY = 'hidden';
      }
    } else {
      el.style.overflowY = 'hidden';
    }
    el.style.height = `${target}px`;
  }

  // Resize on mount AND whenever `value` changes from outside (e.g. when
  // a preset template populates the field, or a dictation transcript is
  // appended). useLayoutEffect so the user never sees the wrong height.
  useLayoutEffect(() => {
    if (innerRef.current) resize(innerRef.current);
  }, [value]);

  // Re-measure on viewport resize (rotation, software keyboard) — in
  // Capacitor WebViews the available width can change after the first
  // paint.
  useEffect(() => {
    function onResize() {
      if (innerRef.current) resize(innerRef.current);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <textarea
      ref={innerRef}
      rows={minRows}
      value={value}
      onInput={(e) => {
        resize(e.currentTarget);
        onInput?.(e);
      }}
      style={{ resize: 'none', overflowY: 'hidden', ...style }}
      {...rest}
    />
  );
});
